/**
 * LibraryPatch.tsx — Injects "Add/Remove FakeAppId" button into Steam Library game detail pages.
 * Uses routerHook.addPatch + createReactTreePatcher (same pattern as HLTB/decky plugins).
 * Uses ButtonItem from @decky/ui for proper gamepad focus support.
 * Checks SLS config to show ADD or REMOVE based on current state.
 */
import {
  afterPatch,
  findInReactTree,
  createReactTreePatcher,
  appDetailsClasses,
  ButtonItem,
  findModuleChild,
} from "@decky/ui";

const SP_REACT = (window as any).SP_REACT;
const h = SP_REACT?.createElement?.bind(SP_REACT);
const useState = SP_REACT?.useState;
const useEffect = SP_REACT?.useEffect;

let _serverAPI: any = null;
let _routerHook: any = null;

// Cache of app_ids that have cr0wbar fixes available
const cr0wbarFixCache = new Set<string>();
let _cr0wbarLoaded = false;

export async function refreshCr0wbarFixes(): Promise<void> {
  try {
    const r = await _serverAPI?.callPluginMethod("fetch_cr0wbar_fix_list", {});
    if (r?.result?.app_ids) {
      cr0wbarFixCache.clear();
      for (const id of r.result.app_ids) {
        cr0wbarFixCache.add(String(id));
      }
      _cr0wbarLoaded = true;
      console.log("[Hollow] cr0wbar fix list loaded:", cr0wbarFixCache.size, "fixes");
    }
  } catch (e) {
    console.log("[Hollow] cr0wbar fix list fetch error:", e);
  }
}

// Cache of app_ids that already have FakeAppId entries
const fakeAppIdsCache = new Set<string>();

async function refreshFakeAppIds(): Promise<void> {
  try {
    const r = await _serverAPI?.callPluginMethod("get_fake_app_ids", {});
    if (r?.result?.ok) {
      fakeAppIdsCache.clear();
      for (const id of (r.result.ids || [])) {
        fakeAppIdsCache.add(String(id));
      }
    }
  } catch {}
}

function notify(title: string, body: string): void {
  _serverAPI?.toaster?.toast?.({ title, body, duration: 5000 });
}

/** Initialize the Library page patch. Returns cleanup function. */
export function initLibraryPatch(serverAPI: any): () => void {
  _serverAPI = serverAPI;
  _routerHook = serverAPI?.routerHook;

  if (!h || !useState) {
    console.log("[Hollow] SP_REACT not available, Library patch disabled");
    return () => {};
  }

  if (!_routerHook) {
    console.log("[Hollow] routerHook not available, Library patch disabled");
    return () => {};
  }

  // Pre-fetch FakeAppIds on init
  refreshFakeAppIds();

  const patch = _routerHook.addPatch("/library/app/:appid", (routerTree: any) => {
    const routeProps = findInReactTree(routerTree, (x: any) => !!x?.renderFunc);
    if (!routeProps) return routerTree;

    const patchHandler = createReactTreePatcher(
      [
        (tree: any) => {
          const children = findInReactTree(
            tree,
            (x: any) => x?.props?.children?.props?.overview
          )?.props?.children;
          return children || null;
        },
      ],
      (_args: any[], ret?: any) => {
        try {
          const container = findInReactTree(
            ret,
            (x: any) =>
              Array.isArray(x?.props?.children) &&
              x?.props?.className?.includes?.(appDetailsClasses?.InnerContainer)
          );

          if (container?.props?.children) {
            const children = container.props.children;

            const overview = findInReactTree(
              ret,
              (x: any) => x?.props?.overview
            )?.props?.overview;
            const appId = overview?.appid?.toString();

            if (appId) {
               const FakeAppIdButton = () => {
                 const [isAdded, setIsAdded] = useState(fakeAppIdsCache.has(appId));
                 const [busy, setBusy] = useState(false);

                 useEffect(() => {
                   setIsAdded(fakeAppIdsCache.has(appId));
                 }, []);

                 const handleClick = async () => {
                   setBusy(true);
                   try {
                     if (isAdded) {
                       const r = await _serverAPI?.callPluginMethod("remove_fake_app_id", { app_id: appId });
                       console.log("[Hollow] remove_fake_app_id response:", JSON.stringify(r));
                       if (r?.result?.ok) {
                         fakeAppIdsCache.delete(appId);
                         setIsAdded(false);
                         notify("Hollow-decky", `Removed FakeAppId for ${appId}`);
                       } else {
                         notify("Hollow-decky", r?.result?.error || `Failed to remove FakeAppId for ${appId}`);
                       }
                     } else {
                       const r = await _serverAPI?.callPluginMethod("add_fake_app_id", { app_id: appId });
                       console.log("[Hollow] add_fake_app_id response:", JSON.stringify(r));
                       if (r?.success === false) {
                         notify("Hollow-decky", `Error: ${r?.result || "unknown"}`);
                       } else if (r?.result?.ok) {
                         const msg = r?.result?.already ? `FakeAppId already set for ${appId}` : `FakeAppId added for ${appId}: 480`;
                         fakeAppIdsCache.add(appId);
                         setIsAdded(true);
                         notify("Hollow-decky", msg);
                       } else if (r?.result?.error) {
                         notify("Hollow-decky", r.result.error);
                       } else {
                         notify("Hollow-decky", `Failed: ${JSON.stringify(r?.result || r)}`);
                       }
                     }
                   } catch (e) {
                     console.log("[Hollow] FakeAppId error:", e);
                     notify("Hollow-decky", `Error: ${e}`);
                   }
                   setBusy(false);
                 };

                 return h(
                   ButtonItem,
                   {
                     key: "hollow-fakeappid",
                     onClick: busy ? undefined : handleClick,
                     disabled: busy,
                   },
                   isAdded ? "REMOVE FAKEAPPID" : "ADD FAKEAPPID"
                 );
               };

               const Cr0wbarFixButton = () => {
                 const hasFix = cr0wbarFixCache.has(appId);
                 const [installing, setInstalling] = useState(false);
                 const [installed, setInstalled] = useState(false);

                 useEffect(() => {
                   // Load cr0wbar fix list on first Library page visit
                   if (!_cr0wbarLoaded) {
                     refreshCr0wbarFixes();
                   }
                 }, []);

                 if (!hasFix) return null;

                 const handleInstall = async () => {
                   setInstalling(true);
                   try {
                     notify("Hollow-decky", `Installing cr0wbar fix for ${appId}...`);
                     const r = await _serverAPI?.callPluginMethod("install_cr0wbar_fix", { app_id: appId });
                     if (r?.result?.ok) {
                       setInstalled(true);
                       notify("Hollow-decky", `Cr0wbar fix installed for ${appId}`);
                     } else {
                       notify("Hollow-decky", r?.result?.error || `Failed to install fix for ${appId}`);
                     }
                   } catch (e) {
                     console.log("[Hollow] cr0wbar fix error:", e);
                     notify("Hollow-decky", `Error: ${e}`);
                   }
                   setInstalling(false);
                 };

                 return h(
                   ButtonItem,
                   {
                     key: "hollow-cr0wbar-fix",
                     onClick: installing ? undefined : handleInstall,
                     disabled: installing || installed,
                   },
                   installed ? "FIX INSTALLED" : installing ? "INSTALLING FIX..." : "INSTALL FIX"
                 );
               };

                const fakeAppEl = h(FakeAppIdButton, { key: "hollow-fakeappid" });
                const cr0wbarEl = h(Cr0wbarFixButton, { key: "hollow-cr0wbar-fix" });

                // Wrap both buttons in a horizontal row
                const buttonRow = h("div", {
                  key: "hollow-buttons-row",
                  style: { display: "flex", flexDirection: "row", gap: "8px", alignItems: "center" },
                }, ...(cr0wbarEl ? [cr0wbarEl, fakeAppEl] : [fakeAppEl]));

                const spliceIdx = children.findIndex(
                  (child: any) =>
                    child?.props?.childFocusDisabled !== undefined &&
                    child?.props?.navRef !== undefined
                );
                if (spliceIdx > -1) {
                  children.splice(spliceIdx, 0, buttonRow);
                } else {
                  children.splice(1, 0, buttonRow);
                }
            }
          }
        } catch (e) {
          console.log("[Hollow] LibraryPatch error:", e);
        }
        return ret;
      }
    );

    afterPatch(routeProps, "renderFunc", patchHandler);
    return routerTree;
  });

  return () => {
    _routerHook.removePatch("/library/app/:appid", patch);
  };
}

// ── Context Menu Patch (3-line button menu) ───────────────────────────────────
let _contextMenuPatches: (() => void)[] = [];

export function initContextMenuPatch(serverAPI: any): () => void {
  _serverAPI = serverAPI;

  const LibraryContextMenu = findModuleChild((m: any) => {
    if (typeof m !== "object") return undefined;
    for (const prop in m) {
      if (m[prop]?.toString?.()?.includes?.("LibraryContextMenu")) {
        return Object.values(m).find((sibling: any) =>
          sibling?.toString?.()?.includes("navigator:")
        ) as any;
      }
    }
    return undefined;
  });

  if (!LibraryContextMenu?.prototype?.render) {
    console.log("[Hollow] LibraryContextMenu not found, context menu patch disabled");
    return () => {};
  }

  const patch = afterPatch(LibraryContextMenu.prototype, "render", (_args: any[], component: any) => {
    try {
      const appId = component?.props?.overview?.appid?.toString?.()
        || component?.props?.appid?.toString?.();

      if (!appId) return component;

      const children = component?.props?.children;
      if (!Array.isArray(children)) return component;

      // Check if already added
      const alreadyAdded = children.some(
        (child: any) => child?.props?.["data-hollow-fakeappid"]
      );
      if (alreadyAdded) return component;

      const isFake = fakeAppIdsCache.has(appId);
      const hasFix = cr0wbarFixCache.has(appId);
      const fakeLabel = isFake ? "REMOVE FAKEAPPID" : "ADD FAKEAPPID";

      const fakeMenuItem = h(
        ButtonItem,
        {
          key: "hollow-fakeappid",
          "data-hollow-fakeappid": appId,
          onClick: async () => {
            try {
              if (isFake) {
                const r = await _serverAPI?.callPluginMethod("remove_fake_app_id", { app_id: appId });
                if (r?.result?.ok) {
                  fakeAppIdsCache.delete(appId);
                  notify("Hollow-decky", `Removed FakeAppId for ${appId}`);
                } else {
                  notify("Hollow-decky", r?.result?.error || "Failed to remove FakeAppId");
                }
              } else {
                const r = await _serverAPI?.callPluginMethod("add_fake_app_id", { app_id: appId });
                if (r?.result?.ok) {
                  fakeAppIdsCache.add(appId);
                  const msg = r?.result?.already ? `FakeAppId already set for ${appId}` : `FakeAppId added for ${appId}: 480`;
                  notify("Hollow-decky", msg);
                } else {
                  notify("Hollow-decky", r?.result?.error || "Failed to add FakeAppId");
                }
              }
            } catch (e) {
              notify("Hollow-decky", `Error: ${e}`);
            }
          },
        },
        fakeLabel
      );

      const cr0wbarMenuItem = hasFix ? h(
        ButtonItem,
        {
          key: "hollow-cr0wbar-fix",
          "data-hollow-cr0wbar": appId,
          onClick: async () => {
            try {
              notify("Hollow-decky", `Installing cr0wbar fix for ${appId}...`);
              const r = await _serverAPI?.callPluginMethod("install_cr0wbar_fix", { app_id: appId });
              if (r?.result?.ok) {
                notify("Hollow-decky", `Cr0wbar fix installed for ${appId}`);
              } else {
                notify("Hollow-decky", r?.result?.error || `Failed to install fix for ${appId}`);
              }
            } catch (e) {
              notify("Hollow-decky", `Error: ${e}`);
            }
          },
        },
        "INSTALL FIX"
      ) : null;

      const menuItems = [cr0wbarMenuItem, fakeMenuItem].filter(Boolean);

      const propsIdx = children.findIndex((child: any) =>
        findInReactTree(child, (x: any) =>
          x?.onSelected?.toString?.()?.includes("AppProperties")
        )
      );

      if (propsIdx > -1) {
        children.splice(propsIdx, 0, ...menuItems);
      } else {
        children.push(...menuItems);
      }
    } catch (e) {
      console.log("[Hollow] context menu patch error:", e);
    }
    return component;
  });

  _contextMenuPatches.push(patch);
  console.log("[Hollow] LibraryContextMenu patched");

  return () => {
    _contextMenuPatches.forEach(p => { try { p(); } catch {} });
    _contextMenuPatches = [];
  };
}